import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { UserRole, AdminType, User } from '../types'
import './RoleSelector.css'

interface RoleSelectorProps {
  onRoleSelect: (role: UserRole, adminType?: AdminType) => void
  currentUser?: User
}

function RoleSelector({ onRoleSelect, currentUser }: RoleSelectorProps) {
  const navigate = useNavigate()
  const [showAdminTypes, setShowAdminTypes] = useState(false)

  // Функция для получения чешского названия роли
  const getRoleLabel = (role: string | null) => {
    if (!role) return 'Bez role'
    switch (role) {
      case 'executor':
        return 'Řešitel'
      case 'requester':
        return 'Žadatel'
      case 'director':
        return 'Ředitel'
      case 'financist':
        return 'Hlavní ekonom'
      case 'referent':
        return 'Obchodní referent'
      case 'vedouci':
        return 'Vedoucí'
      case 'vedouci_executor':
        return 'Řešitel-Vedoucí'
      case 'zastupce_reditele':
        return 'Zástupce ředitele'
      default:
        return role
    }
  }

  // Определяем, какие роли доступны пользователю
  // IT администратор может выбирать любую роль (универсальный доступ)
  // Логируем для отладки
  console.log('RoleSelector: currentUser =', currentUser)
  console.log('RoleSelector: role =', currentUser?.role, ', admin_type =', currentUser?.admin_type)
  
  const isITAdmin = currentUser?.role === 'admin' && currentUser?.admin_type === 'spravce_it'
  const isBuildingAdmin = currentUser?.role === 'admin' && currentUser?.admin_type === 'spravce_budov'
  const canBeAdmin = currentUser?.role === 'admin'
  // Решитель-ведущий может быть как решителем, так и ведущим
  // Správce budov также может быть решителем
  const canBeExecutor = currentUser?.role === 'executor' || 
                        currentUser?.role === 'vedouci_executor' || 
                        currentUser?.role === 'zastupce_reditele' ||
                        isBuildingAdmin ||
                        currentUser?.role === 'admin' // Все администраторы могут быть решителями
  const canBeRequester = true // requester доступен всем, включая админов
  
  console.log('RoleSelector: canBeAdmin =', canBeAdmin, ', canBeExecutor =', canBeExecutor, ', isITAdmin =', isITAdmin)

  const handleAdminClick = () => {
    if (!canBeAdmin) {
      alert('Nemáte oprávnění k přístupu jako administrátor. Kontaktujte IT administrátora.')
      return
    }
    setShowAdminTypes(true)
  }

  const handleAdminTypeSelect = (adminType: AdminType) => {
    if (!canBeAdmin) {
      alert('Nemáte oprávnění k přístupu jako administrátor.')
      return
    }
    
    // Проверяем, что пользователь может выбрать этот тип администратора
    if (adminType === 'spravce_it' && !isITAdmin) {
      alert('Nemáte oprávnění k přístupu jako správce IT. Přístup pouze pro správce IT.')
      return
    }
    
    // Spravce_budov может выбрать только spravce_budov
    if (adminType === 'spravce_budov' && currentUser?.admin_type === 'spravce_it') {
      // IT администратор может выбрать любой тип, но лучше использовать свой
      // Разрешаем, но предупреждаем
    }
    
    onRoleSelect('admin', adminType)
  }

  const handleExecutorClick = () => {
    if (!canBeExecutor) {
      alert('Nemáte oprávnění k přístupu jako řešitel. Kontaktujte IT administrátora.')
      return
    }
    onRoleSelect('executor')
  }

  const handleRequesterClick = () => {
    onRoleSelect('requester')
  }

  if (showAdminTypes) {
    return (
      <div className="role-selector">
        <div className="role-selector-container">
          <button 
            className="back-to-roles-button"
            onClick={() => setShowAdminTypes(false)}
          >
            ← Zpět
          </button>
          <h1>Vyberte typ administrátora</h1>
          <p className="subtitle">Vyberte svůj typ pro přístup k systému</p>
          
          <div className="role-buttons">
            {isITAdmin && (
              <button 
                className="role-button admin-it"
                onClick={() => handleAdminTypeSelect('spravce_it')}
              >
                <h2>Správce IT</h2>
              </button>
            )}
            
            {(isITAdmin || currentUser?.admin_type === 'spravce_budov') && (
              <button 
                className="role-button admin-buildings"
                onClick={() => handleAdminTypeSelect('spravce_budov')}
              >
                <h2>Správce Budov</h2>
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="role-selector">
      <div className="role-selector-container">
        <button 
          className="back-to-roles-button"
          onClick={() => navigate('/intranet')}
          style={{ marginBottom: '1rem' }}
        >
          ← Zpět
        </button>
        <h1>Hlášení závad</h1>
        <p className="subtitle">Vyberte svou roli pro přístup k systému</p>
        
          <div className="role-buttons">
            <button 
              className={`role-button requester ${!canBeRequester ? 'disabled' : ''}`}
              onClick={handleRequesterClick}
              disabled={!canBeRequester}
              title={!canBeRequester ? 'Tato role není k dispozici' : ''}
            >
              <h2>Žadatel</h2>
              {!canBeRequester && <span className="disabled-badge">Nedostupné</span>}
            </button>
            
            <button 
              className={`role-button admin ${!canBeAdmin ? 'disabled' : ''}`}
              onClick={handleAdminClick}
              disabled={!canBeAdmin}
              title={!canBeAdmin ? 'Nemáte oprávnění jako administrátor' : ''}
            >
              <h2>Administrátor</h2>
              {!canBeAdmin && <span className="disabled-badge">Nedostupné</span>}
            </button>
            
            <button 
              className={`role-button executor ${!canBeExecutor ? 'disabled' : ''}`}
              onClick={handleExecutorClick}
              disabled={!canBeExecutor}
              title={!canBeExecutor ? 'Nemáte oprávnění jako řešitel' : ''}
            >
              <h2>Řešitel</h2>
              {!canBeExecutor && <span className="disabled-badge">Nedostupné</span>}
            </button>
          </div>
          
          {currentUser && (
            <div style={{ 
              marginTop: '2rem', 
              padding: '1rem 1.5rem', 
              background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)', 
              borderRadius: '12px',
              color: 'white',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}>
              <p style={{ margin: 0, fontSize: '1rem', fontWeight: '500' }}>
                <strong>{currentUser.name}</strong>
                {currentUser.admin_type && (
                  <span style={{ marginLeft: '0.5rem', opacity: 0.9 }}>
                    • {currentUser.admin_type === 'spravce_it' ? 'Správce IT' : 'Správce Budov'}
                  </span>
                )}
                {!currentUser.admin_type && currentUser.role !== 'admin' && (
                  <span style={{ marginLeft: '0.5rem', opacity: 0.9 }}>
                    • {getRoleLabel(currentUser.role)}
                  </span>
                )}
                {isITAdmin && (
                  <span style={{ marginLeft: '0.5rem', fontSize: '0.9rem', opacity: 0.95 }}>
                    ✅ Přístup ke všem rolím
                  </span>
                )}
              </p>
            </div>
          )}
      </div>
    </div>
  )
}

export default RoleSelector



